import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { DraftingActions, kStaleDraftsTimestamp } from './slice'
import { selectMatterHasDraftsForConversation } from './selectors'
import { getMatterArtifactDraftsByMatterId } from '@/matter-artifacts/fetch/get-matter-artifact-drafts-by-matter-id'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { AgentV2ServerEventType } from '@/agent-v2/store/schemas'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { selectAgentV2ConversationMatterId } from '@/agent-v2/conversations/store/selectors'

export const draftingListenerMiddleware = createListenerMiddleware()
const startListener = draftingListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

// How long a matter's drafts stay fresh before a non-forced refresh refetches.
const kMatterDraftsCacheDurationMs = 60 * 1000

/**
 * Refresh Matter Artifact Drafts Listener
 *
 * Loads a matter's drafts into the store for the Drafts destination.
 */
startListener({
  actionCreator: DraftingActions.refreshMatterArtifactDrafts,
  effect: async (action, listenerApi) => {
    const { matterId, forceRefresh } = action.payload
    const state = listenerApi.getState().draftingState

    // Skip if a fetch is already in flight, or the cache is still fresh and this isn't forced
    if (state.matterArtifactDraftsLoadingByMatterId[matterId]) return
    const lastFetch = state.matterArtifactDraftsLastRefreshByMatterId[matterId] ?? null
    const cacheExpired = lastFetch === null || Date.now() - lastFetch > kMatterDraftsCacheDurationMs
    if (!cacheExpired && !forceRefresh) return

    try {
      listenerApi.dispatch(DraftingActions.setMatterArtifactDraftsLoadingForMatter({ matterId, loading: true }))
      listenerApi.dispatch(DraftingActions.setMatterArtifactDraftsErrorForMatter({ matterId, error: false }))

      const matterArtifactDrafts = await getMatterArtifactDraftsByMatterId(matterId)

      listenerApi.dispatch(MatterArtifactsActions.upsertMatterArtifactVersions(matterArtifactDrafts.map((draft) => draft.artifact_version)))
      listenerApi.dispatch(
        DraftingActions.setDraftArtifactVersionConversationIds(
          Object.fromEntries(matterArtifactDrafts.map((draft) => [draft.artifact_version.id, draft.conversation_id]))
        )
      )
      listenerApi.dispatch(
        DraftingActions.setMatterArtifactDraftVersionIdsForMatter({
          matterId,
          artifactVersionIds: matterArtifactDrafts.map((draft) => draft.artifact_version.id),
        })
      )
    } catch {
      // The previously stored ids are left in place so a failed refresh keeps showing cached rows.
      listenerApi.dispatch(DraftingActions.setMatterArtifactDraftsErrorForMatter({ matterId, error: true }))
    } finally {
      listenerApi.dispatch(DraftingActions.setMatterArtifactDraftsLoadingForMatter({ matterId, loading: false }))

      // A result that predates a mid-fetch invalidation must not stamp the cache fresh and bury it.
      const invalidatedMidFetch =
        listenerApi.getState().draftingState.matterArtifactDraftsLastRefreshByMatterId[matterId] === kStaleDraftsTimestamp &&
        lastFetch !== kStaleDraftsTimestamp
      if (invalidatedMidFetch) {
        listenerApi.dispatch(DraftingActions.refreshMatterArtifactDrafts({ matterId, forceRefresh: true }))
      } else {
        listenerApi.dispatch(DraftingActions.setMatterArtifactDraftsLastRefreshForMatter({ matterId, timestamp: Date.now() }))
      }
    }
  },
})

/**
 * Invalidate Drafts On Draft Completion
 *
 * A completed draft is now queryable from the matter drafts endpoint.
 */
startListener({
  actionCreator: AgentV2TranscriptActions.serverEventReceived,
  effect: (action, listenerApi) => {
    if (action.payload.event.type !== AgentV2ServerEventType.DRAFT_COMPLETED) return

    const matterId = selectAgentV2ConversationMatterId(listenerApi.getState(), { conversationId: action.payload.conversationId })
    if (matterId) listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
  },
})

/**
 * Invalidate Drafts On Matter Artifact Version Advance
 *
 * Manual saves and accepted chat edits create a new immutable version. Resolve its Matter from
 * the previously cached current version, then refetch authoritative draft heads. Ignore generation
 * polling, historical versions, and repeated hydration of the current version.
 */
startListener({
  actionCreator: MatterArtifactsActions.upsertMatterArtifactVersion,
  effect: (action, listenerApi) => {
    const originalState = listenerApi.getOriginalState()
    const previousCurrentVersion = Object.values(originalState.matterArtifactsState.versionsById).find(
      (version) => version?.matter_artifact_id === action.payload.matter_artifact_id && version.is_current
    )
    if (!action.payload.is_current || !previousCurrentVersion || previousCurrentVersion.id === action.payload.id) return

    const matterId = findMatterIdForMatterArtifact(originalState, action.payload.matter_artifact_id)
    if (matterId) listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
  },
})

/**
 * Invalidate Drafts On Conversation Delete
 */
startListener({
  actionCreator: AgentV2ConversationsActions.deleteConversation,
  effect: (action, listenerApi) => {
    const { conversationId } = action.payload
    const originalState = listenerApi.getOriginalState()
    const matterId = selectAgentV2ConversationMatterId(originalState, { conversationId })
    if (!matterId || !selectMatterHasDraftsForConversation(originalState, { matterId, conversationId })) return

    listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
  },
})

function findMatterIdForMatterArtifact(state: RootState, matterArtifactId: string): string | null {
  const versionIds = new Set<string>()
  for (const version of Object.values(state.matterArtifactsState.versionsById)) {
    if (version?.matter_artifact_id === matterArtifactId) versionIds.add(version.id)
  }

  for (const [matterId, draftVersionIds] of Object.entries(state.draftingState.matterArtifactDraftVersionIdsByMatterId)) {
    if (draftVersionIds?.some((versionId) => versionIds.has(versionId))) return matterId
  }
  return null
}
